import { VectorMeta } from "@nens/rana/api-v1";

import { CompleteFile } from "../../../types/file-reference-with-file";
import { getFileLayers, getFileName } from "../../../utils/files";
import type { SelectedFileOrLayer } from "../../map-layers";

interface UseFileLayerSelectionProps {
  selectedItems: SelectedFileOrLayer[];
  onSelectionChange: (items: SelectedFileOrLayer[]) => void;
}

export function getSingleLayerForFile(file: CompleteFile): SelectedFileOrLayer | null {
  // Returns null if the file should not be selectable as a whole (e.g. because it has multiple layers), otherwise returns the SelectedFileOrLayer that would be added when selecting the file as a whole.
  let newLayer: SelectedFileOrLayer | null = null;
  if (file.file.descriptor.dataType === "raster") {
    // For raster files, we add the file itself as a "layer", since there are no layers in raster files.
    newLayer = {
      file,
      name: getFileName(file),
    };
  } else if (file.file.descriptor.dataType === "vector") {
    // We only show file check boxes in case there is a single layer, so pick the first.
    if ((file.file.descriptor.meta as VectorMeta).layers.length !== 1) {
      return null;
    }
    const layer = (file.file.descriptor.meta as VectorMeta).layers[0];

    newLayer = {
      file,
      name: layer.name,
      layerInFile: layer.id,
    };
  } else if (file.file.descriptor.dataType === "scenario") {
    // For scenarios, take the code from the descriptor directly, not from meta.
    if (file.file.descriptor.layers?.length !== 1) {
      return null;
    }
    const layer = file.file.descriptor.layers[0];
    newLayer = {
      file,
      name: layer.name,
      layerInFile: layer.id,
    };
  }
  return newLayer;
}

export const useFileLayerSelection = ({
  selectedItems,
  onSelectionChange,
}: UseFileLayerSelectionProps) => {
  const handleFileCheckbox = (checked: boolean, file: CompleteFile) => {
    if (checked) {
      const newLayer = getSingleLayerForFile(file);
      if (newLayer) {
        onSelectionChange([...selectedItems, newLayer]);
      }
    } else {
      onSelectionChange(selectedItems.filter((item) => item.file.path !== file.path));
    }
  };

  const handleAllLayerCheckbox = (checked: boolean, file: CompleteFile) => {
    if (checked) {
      // Add all layers from this file to the selection
      const newLayers = getFileLayers(file).filter(
        (layer) =>
          !selectedItems.some(
            (item) => item.file.path === file.path && item.layerInFile === layer.layerInFile
          )
      );
      onSelectionChange([...selectedItems, ...newLayers]);
    } else {
      // Remove all layers from this file
      onSelectionChange(selectedItems.filter((item) => item.file.path !== file.path));
    }
  };

  const handleLayerCheckbox = (
    checked: boolean,
    file: CompleteFile,
    name: string,
    layerInFile: string | undefined
  ) => {
    if (checked) {
      onSelectionChange([...selectedItems, { file, name, layerInFile }]);
    } else {
      onSelectionChange(
        selectedItems.filter(
          (item) =>
            !(
              // check for both file and layerInFile
              (item.file.path === file.path && item.layerInFile === layerInFile)
            )
        )
      );
    }
  };

  const isFileSelected = (fileId: string) =>
    // Checks that file can be selected as a whole, and if so, whether it is selected. We check if the file can be selected as a whole by checking if there is a single layer for the file, since we only show the file-level checkbox in that case.
    selectedItems.some(
      (item) => item.file.path === fileId && getSingleLayerForFile(item.file)?.file.path !== null
    );

  const isLayerSelected = (filePath: string, layerInFile: string | undefined) =>
    selectedItems.some(
      // check for both file and layer id in case layers have same id
      (item) => item.file.path === filePath && item.layerInFile === layerInFile
    );

  return {
    handleFileCheckbox,
    handleLayerCheckbox,
    handleAllLayerCheckbox,
    isFileSelected,
    isLayerSelected,
  };
};
